import { tenantApi } from '@shared/api/tenantApi';
import { unwrap } from '@shared/api/unwrap';

/**
 * Billing — `GET /public/billing/invoices`
 * Returns the member's financial account plus paginated transactions.
 * Pass `type=all` for full history, or `invoice` | `receipt` | `creditnote`.
 */

export type BillingTransactionType = 'all' | 'invoice' | 'receipt' | 'creditnote' | 'refund';

export interface BillingAccount {
  account_id: string;
  account_code?: string | null;
  currency_code: string;
  balance_cents: number;
  /** Balance in major currency units (e.g. rands, not cents). */
  balance: number;
  updated_at?: string | null;
}

export interface BillingTransaction {
  transaction_id: string;
  transaction_type: string;
  impact_type: string;
  status: string;
  document_number?: string | null;
  reference_number?: string | null;
  description?: string | null;
  order_id?: string | null;
  total_in_vat_cents: number;
  total_in_vat: number;
  amount_due_cents: number;
  amount_due: number;
  unallocated_amount_cents: number;
  unallocated_amount: number;
  currency_code: string;
  created_at: string;
}

export interface BillingPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface BillingPage {
  account: BillingAccount;
  transactions: BillingTransaction[];
  pagination: BillingPagination;
}

interface BillingPayload {
  account?: BillingAccount;
  transactions?: { data?: BillingTransaction[]; pagination?: BillingPagination };
}

function normalizeAccount(raw: BillingAccount): BillingAccount {
  const balanceCents = Number(raw.balance_cents ?? 0);
  const balance =
    raw.balance != null && Number.isFinite(Number(raw.balance))
      ? Number(raw.balance)
      : balanceCents / 100;

  return {
    account_id: raw.account_id,
    account_code: raw.account_code ?? null,
    currency_code: (raw.currency_code || 'ZAR').toUpperCase(),
    balance_cents: balanceCents,
    balance,
    updated_at: raw.updated_at ?? null,
  };
}

function normalizeTransaction(raw: BillingTransaction): BillingTransaction {
  const totalCents = Number(raw.total_in_vat_cents ?? 0);
  const dueCents = Number(raw.amount_due_cents ?? 0);
  const unallocCents = Number(raw.unallocated_amount_cents ?? 0);

  return {
    ...raw,
    total_in_vat_cents: totalCents,
    total_in_vat:
      raw.total_in_vat != null && Number.isFinite(Number(raw.total_in_vat))
        ? Number(raw.total_in_vat)
        : totalCents / 100,
    amount_due_cents: dueCents,
    amount_due:
      raw.amount_due != null && Number.isFinite(Number(raw.amount_due))
        ? Number(raw.amount_due)
        : dueCents / 100,
    unallocated_amount_cents: unallocCents,
    unallocated_amount:
      raw.unallocated_amount != null && Number.isFinite(Number(raw.unallocated_amount))
        ? Number(raw.unallocated_amount)
        : unallocCents / 100,
    currency_code: (raw.currency_code || 'ZAR').toUpperCase(),
  };
}

export async function fetchBillingPage(
  options: { page?: number; limit?: number; type?: BillingTransactionType } = {},
): Promise<BillingPage | null> {
  const { page = 1, limit = 50, type = 'all' } = options;
  const { data } = await tenantApi.get('/public/billing/invoices', {
    params: { page, limit, type },
  });
  const payload = unwrap<BillingPayload>(data);
  if (!payload?.account) return null;

  const transactions = (payload.transactions?.data ?? []).map(normalizeTransaction);

  return {
    account: normalizeAccount(payload.account),
    transactions,
    pagination: payload.transactions?.pagination ?? {
      page,
      limit,
      total: transactions.length,
      pages: 1,
    },
  };
}

/**
 * Transaction types the API renders as a document. Journals are internal ledger
 * adjustments with no PDF, so those rows stay inert. Mirrors
 * `DOCUMENT_TRANSACTION_TYPES` in tenant-api's CustomerBillingService.
 */
const DOCUMENT_TYPES = new Set(['invoice', 'receipt', 'creditnote', 'refund']);

export function hasTransactionDocument(tx: BillingTransaction): boolean {
  return DOCUMENT_TYPES.has(tx.transaction_type);
}

/** The transaction's PDF (line items included), generated on demand. */
export async function downloadTransactionPdfBlob(transactionId: string): Promise<Blob> {
  const { data } = await tenantApi.get(`/public/billing/transactions/${transactionId}/pdf`, {
    responseType: 'blob',
  });
  return data as Blob;
}

/* ── Statements + ageing (gated by the financials:statements license) ─────── */

export interface MemberStatement {
  statement_id: string;
  period_from: string;
  period_to: string;
  closing_balance: number; // major units
  currency_code: string | null;
  created_at: string;
}

export interface MemberAgeing {
  as_of_date: string;
  total_arrears: number;
  current: number;
  days_30: number;
  days_60: number;
  days_90: number;
  days_120_plus: number;
  account_balance: number;
  currency_code: string | null;
}

const toMajor = (cents: unknown): number => (Number(cents ?? 0) || 0) / 100;

/** The member's generated statements, newest first. */
export async function fetchStatements(): Promise<MemberStatement[]> {
  const { data } = await tenantApi.get('/public/billing/statements');
  const list = unwrap<any[]>(data) ?? [];
  return list.map((s) => ({
    statement_id: s.statement_id,
    period_from: s.period_from,
    period_to: s.period_to,
    closing_balance: toMajor(s.closing_balance),
    currency_code: s.currency_code ? String(s.currency_code).toUpperCase() : null,
    created_at: s.created_at,
  }));
}

/** Short-lived signed PDF URL for a statement the member owns. */
export async function fetchStatementDownloadUrl(statementId: string): Promise<string | null> {
  const { data } = await tenantApi.get(`/public/billing/statements/${statementId}/download`);
  const payload = unwrap<{ url?: string }>(data);
  return payload?.url ?? null;
}

/** The member's latest ageing summary (major units), or null if none yet. */
export async function fetchAgeingSummary(): Promise<MemberAgeing | null> {
  const { data } = await tenantApi.get('/public/billing/ageing');
  const a = unwrap<any>(data);
  if (!a) return null;
  return {
    as_of_date: a.as_of_date,
    total_arrears: toMajor(a.total_arrears),
    current: toMajor(a.current),
    days_30: toMajor(a.days_30),
    days_60: toMajor(a.days_60),
    days_90: toMajor(a.days_90),
    days_120_plus: toMajor(a.days_120_plus),
    account_balance: toMajor(a.account_balance),
    currency_code: a.currency_code ? String(a.currency_code).toUpperCase() : null,
  };
}

/* ── Pay now (outstanding account balance) ────────────────────────────────── */

/** Whether the "Pay now" flow is available (tenant has a default payment channel + amount due). */
export async function fetchPaymentAvailability(): Promise<boolean> {
  const { data } = await tenantApi.get('/public/billing/payment-availability');
  const payload = unwrap<{ available?: boolean }>(data);
  return !!payload?.available;
}

/** Generate a payment link for the outstanding account balance; returns the gateway URL. */
export async function payAccountBalance(returnUrl?: string): Promise<string | null> {
  const { data } = await tenantApi.post('/public/billing/pay', { return_url: returnUrl });
  const payload = unwrap<{ payment_url?: string }>(data);
  return payload?.payment_url ?? null;
}

/**
 * Pay an invoice by generating a hosted payment link.
 */
export async function payInvoice(
  invoiceId: string,
  returnUrl: string,
): Promise<{ redirect_url?: string; status: string }> {
  const { data } = await tenantApi.post<{ data?: { payment_url?: string } }>(
    '/public/payment-links',
    { invoice_id: invoiceId, return_url: returnUrl },
  );
  return { redirect_url: data.data?.payment_url, status: 'pending' };
}
